import { useState } from 'react';
import { IconQuestionMark } from '@tabler/icons';
import { Badge, Button, Card, CardContent, CardFooter, PricePair } from '#root/components/ui';
import type { MatchGroupDTO, MatchOrderDTO } from '#root/models/PrepaidMatchDTO';
import { boostLabel, deliverByInfo } from './matchFormat';
import MatchPriceDialog from './MatchPriceDialog';
import styles from './match.module.scss';

/**
 * Dominio del link de compra ("www.apple.com"), igual que `url_host` en los pedidos regulares.
 * `null` si no hay link o no es http(s): el EXPRESS puede traer una url armada a mano.
 */
const hostOf = (url: string | null): string | null => {
  if (!url || !/^https?:\/\//.test(url)) return null;
  try {
    return new URL(url).hostname || null;
  } catch (e) {
    return null;
  }
};

interface MatchGroupCardProps {
  group: MatchGroupDTO;
  /** El viajero puede reservar: "Reservar"; si no, "Lo quiero" abre el checklist de requisitos */
  eligible: boolean;
  /** `server_time` del feed: "hoy" en Lima para la fecha de entrega */
  serverTime: string;
  onWant: (group: MatchGroupDTO) => void;
  /** Abre el modal de Envío Nacional (badge de los pedidos con ese envío) */
  onShippingInfo: () => void;
}

const MatchGroupCard = ({
  group,
  eligible,
  serverTime,
  onWant,
  onShippingInfo,
}: MatchGroupCardProps) => {
  const deadline = deliverByInfo(group.deliver_by, serverTime);
  const plural = group.orders.length > 1;
  // El pedido del detalle de precio se conserva al cerrar: el Modal sigue montado para animar
  const [priceOrder, setPriceOrder] = useState<MatchOrderDTO | null>(null);
  const [priceOpen, setPriceOpen] = useState(false);

  // Solo `available` tiene CTA; los demás estados dicen por qué no se puede reservar.
  // `mine` no llega aquí: `MatchFeed` lo filtra porque ya se muestra en "Tus reservas".
  const action = (() => {
    switch (group.availability) {
      case 'taken':
        return (
          <Badge className={styles.status} variant="secondary">
            Reservado por otro viajero
          </Badge>
        );
      case 'blocked':
        return (
          <Badge className={styles.status} variant="outline">
            No disponible para ti
          </Badge>
        );
      default:
        return (
          <Button className={styles.want} onClick={() => onWant(group)}>
            {eligible ? 'Reservar' : plural ? 'Los quiero' : 'Lo quiero'}
          </Button>
        );
    }
  })();

  return (
    <Card noHover>
      {/* Reservado por otro: solo se atenúa el contenido, el badge del pie queda legible */}
      <CardContent
        className={`${styles.orders} ${group.availability === 'taken' ? styles.taken : ''}`}>
        {group.orders.map((order) => {
          // El nombre ya suele traer la capacidad ("iPhone 18 Pro 256GB"): no repetirla
          const capacity = order.capacity && !order.name.includes(order.capacity) ? order.capacity : null;
          const host = hostOf(order.purchase_url);
          const image = (
            <img className={styles.image} src={order.image} alt={order.name} loading="lazy" />
          );

          return (
            <div key={order.idp} className={styles.order}>
              {/* Foto, título y chip abren el link de compra, como en los pedidos regulares */}
              {host ? (
                <a
                  className={styles.imageLink}
                  href={order.purchase_url}
                  target="_blank"
                  rel="noopener noreferrer">
                  {image}
                </a>
              ) : (
                image
              )}
              <div className={styles.info}>
                <h3 className={styles.name}>
                  {host ? (
                    <a
                      className={styles.nameLink}
                      href={order.purchase_url}
                      target="_blank"
                      rel="noopener noreferrer">
                      {order.name}
                    </a>
                  ) : (
                    order.name
                  )}
                  {order.quantity > 1 && <span className={styles.qty}> ×{order.quantity}</span>}
                </h3>
                {(order.detail || capacity) && (
                  <p className={styles.meta}>
                    {order.detail && <span className={styles.detail}>{order.detail}</span>}
                    {order.detail && capacity && ' · '}
                    {capacity}
                  </p>
                )}
                {host && (
                  <div className={styles.chips}>
                    <a
                      className={styles.hostChip}
                      href={order.purchase_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Link de compra: ${host}`}>
                      {host}
                    </a>
                  </div>
                )}
              </div>
              {/* Precio real + comisión neta: abre el detalle (qué incluye, tax y total).
                  Con booster (`boost`, que un BE viejo no manda) la comisión va en morado */}
              <PricePair
                className={styles.amounts}
                tone={order.boost > 0 ? 'match' : undefined}
                priceLabel="Precio real"
                price={order.price}
                commissionLabel="Comisión neta"
                commission={order.earning}
                aria-label="Ver detalle de precio real y comisión neta"
                onClick={() => {
                  setPriceOrder(order);
                  setPriceOpen(true);
                }}
              />
            </div>
          );
        })}
      </CardContent>
      <CardFooter className={styles.footer}>
        <div className={styles.footerInfo}>
          {group.delivery_scope === 'Lima' ? (
            <Badge size="sm">{group.delivery_scope}</Badge>
          ) : (
            // Como en los pedidos regulares: el "?" abre el modal de Envío Nacional
            <Badge
              size="sm"
              variant="shipping"
              onClick={onShippingInfo}
              icon={<IconQuestionMark size={14} />}>
              {group.delivery_scope}
            </Badge>
          )}
          {/* Con booster, un solo badge (mejora + fecha); un BE viejo no manda `boosted` */}
          {group.boosted ? (
            <Badge size="sm" variant="match">
              {boostLabel(group.deliver_by, serverTime)}
            </Badge>
          ) : (
            deadline &&
            (deadline.urgent ? (
              <Badge size="sm" variant="warning">
                {deadline.label}
              </Badge>
            ) : (
              <Badge size="sm" variant="outline">
                {deadline.label}
              </Badge>
            ))
          )}
        </div>
        {action}
      </CardFooter>
      {priceOrder && (
        <MatchPriceDialog
          open={priceOpen}
          order={priceOrder}
          onClose={() => setPriceOpen(false)}
        />
      )}
    </Card>
  );
};

export default MatchGroupCard;
